/**
 * CitySelector Component
 * Searchable dropdown selector for filtering by city/location
 */

import { useState } from 'react'
import { useCities } from '@features/professions/hooks'
import { Button } from '@shared/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shared/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@shared/components/ui/command'
import { Check, ChevronDown, MapPin } from 'lucide-react'
import { cn } from '@shared/lib/utils'
import type { LucideIcon } from 'lucide-react'

// ============================================================================
// Types
// ============================================================================

interface CitySelectorProps {
  value?: string
  onChange: (cityId: string) => void
  placeholder?: string
  className?: string
  disabled: boolean;
  rightIcon?: LucideIcon
}

// ============================================================================
// Component
// ============================================================================

export function CitySelector({
  value,
  onChange,
  placeholder = 'Todas las ciudades',
  className,
  disabled,
  rightIcon: RightIcon,
}: CitySelectorProps) {
  const { data: cities, isLoading } = useCities()
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  // Filter cities based on search term
  const filteredCities = cities?.filter((city) =>
    city.name.toLowerCase().includes(searchValue.toLowerCase())
  ) || []

  // Get selected city name for display
  const selectedCity = cities?.find((city) => city.id === value)
  const displayValue = selectedCity ? selectedCity.name : placeholder

  const handleSelect = (cityId: string) => {
    onChange(cityId === 'all' ? '' : cityId)
    setOpen(false)
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      setSearchValue('') // Clear search when dropdown closes
    }
  }

  // Calculate padding based on icon presence
  const leftPadding = RightIcon ? 'pl-3' : 'pl-10' // No left padding when right icon is used
  const rightPadding = RightIcon ? 'pr-10' : 'pr-3'

  return (
    <div className={`relative ${className}`}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between bg-input border-border shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring h-9 rounded-md py-2 hover:bg-input focus:bg-input hover:text-current focus:text-current text-base md:text-sm font-normal",
              leftPadding,
              rightPadding
            )}
            disabled={isLoading || disabled}
          >
            <span className="truncate">{displayValue}</span>
            <ChevronDown className="-ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        {/* Left MapPin icon (only when no right icon) */}
        {!RightIcon && (
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
        )}

        {/* Right icon (positioned right of chevron) */}
        {RightIcon && (
          <RightIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
        )}

        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 border-border shadow-sm" align="start">
          <Command>
            <CommandInput
              placeholder="Buscar ciudad..."
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList className="max-h-60 overflow-y-auto">
              <CommandEmpty>No se encontraron ciudades.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="all"
                  onSelect={() => handleSelect('all')}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      !value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  Todas las ciudades
                </CommandItem>

                {filteredCities.map((city) => (
                  <CommandItem
                    key={city.id}
                    value={city.name}
                    onSelect={() => handleSelect(city.id)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === city.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {city.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
